import React, { useState } from "react";
import { Alert, Button } from "react-bootstrap";
import { useDispatch } from "react-redux";
import Contract from "domain/contract";
import useStartup from "common/ui/useStartup";
import contractService, { SetManualErpIdResponse } from "./contractService";
import Permissions from "common/security/permissions";
import usePermission from "common/security/usePermission";
import { ModalState } from "common/ui/useModalState";
import ModalWrapper from "common/ui/Modal";
import ValidationError from "common/validationError";
import loggingService from "common/logging/loggingService";
import notificationService from "common/notificationService";
import ManualErpIdModal from "./ManualErpIdModal";
import ErpIdVerificationModal from "./ErpIdVerificationModal";
import ConfirmErpIdModal from "./ConfirmErpIdModal";
import { ContractUpdated } from "./contracts.actions";

interface SyncContractToErpRetryModalProps {
    modalState: ModalState<{ contract: Contract, hideRetrySync: boolean }>;
};

const SyncContractToErpRetryModal: React.FC<SyncContractToErpRetryModalProps> = (props: SyncContractToErpRetryModalProps) => {

    const [failureReason, setFailureReason] = useState<string>(null);

    useStartup(() => {
        contractService.getErpSyncFailureReason(props.modalState.data.contract.id).then(m => setFailureReason(m));
    });

    return props.modalState.data.hideRetrySync ?
        <ModalFooter {...props} /> :
        <ModalWrapper title="ERP Contract Sync Errors"
            onAdvance={() => { }}
            onCancel={() => { props.modalState.hide(); }}
            advanceButtonText="Retry Sync"
            footer={<ModalFooter {...props} />}>
            {props.modalState.data.contract.isSyncingToErp && <Alert variant="warning">
                <strong>Sync in progress...</strong> Please wait until the sync is complete before retrying.
            </Alert>}
            <div className="inline" style={{ whiteSpace: 'pre-wrap' }}>
                {failureReason}
            </div>
        </ModalWrapper>;
}


const ModalFooter: React.FC<SyncContractToErpRetryModalProps> = React.memo(props => {
    const dispatch = useDispatch();
    const contract = props.modalState.data.contract;

    const canRetrySync = usePermission(Permissions.RetrySync);
    const canEnterErpId = usePermission(Permissions.EnterErpId);
    const hideRetrySync = props.modalState.data.hideRetrySync;

    const [showManualErpIdModal, setShowManualErpIdModal] = useState(hideRetrySync);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showVerificationModal, setShowVerificationModal] = useState(false);

    const [currentErpId, setCurrentErpId] = useState<string>('');
    const [response, setResponse] = useState<SetManualErpIdResponse | null>(null);
    const [errors, setErrors] = useState<string[]>([]);

    async function refreshContract() {
        const updatedContract = await contractService.getContract(contract.internalId);
        dispatch(new ContractUpdated(updatedContract).toObject());
        props.modalState.setData({ contract: updatedContract, hideRetrySync: hideRetrySync });
    }

    async function retryErpSync() {
        await contractService.retryContractSyncingToErp(contract);
        props.modalState.hide();
    }

    function handleAddErpId() {
        setShowManualErpIdModal(true);
        setErrors([]);
        setResponse(null);
    }

    function handleSubmitErpId(erpId: string) {
        setCurrentErpId(erpId);
        setShowManualErpIdModal(false);
        setShowConfirmModal(true);
    }

    async function handleConfirmErpId() {
        try {
            const result = await contractService.setManualErpId(contract.id, currentErpId);
            setResponse(result);
            setErrors([]);

            setShowConfirmModal(false);

            if (result.isVerified) {
                await refreshContract();
                notificationService.success('ERP ID saved and verified successfully');
                props.modalState.hide();
            } else {
                setShowVerificationModal(true);
            }
        } catch (error: unknown) {
            setErrors((error as ValidationError).errors ?? ["An unknown error occurred"]);
            loggingService.error("Error setting manual ERP ID", error as Error);

            // close confirmation and reopen manual entry modal with errors
            setShowConfirmModal(false);
            setShowManualErpIdModal(true);
        }
    }

    function handleCancelConfirm() {
        setShowConfirmModal(false);
        setShowManualErpIdModal(true);
    }

    async function handleVerifyErpId() {
        try {
            const result = await contractService.verifyManualErpId(contract.id);
            setResponse(result);

            if (result.isVerified) {
                await refreshContract();
                notificationService.success('ERP ID verified successfully');
                setShowVerificationModal(false);
                props.modalState.hide();
            } else {
                notificationService.warning(result.message);
            }
        } catch (error: unknown) {
            notificationService.error("Failed to verify ERP ID");
            loggingService.error("Error verifying ERP ID", error as Error);
        }
    }

    async function handleRetrySubmit() {
        handleSubmitErpId(currentErpId);
    }

    async function handleSaveWithoutVerification() {
        await refreshContract();
        notificationService.info("ERP ID saved without verification");
        setShowVerificationModal(false);
        props.modalState.hide();
    }

    function handleCloseVerificationModal() {
        setShowVerificationModal(false);
        setResponse(null);
    }

    return <div className="pull-right">
        {!props.modalState.data.hideRetrySync && <><Button variant="secondary" className="ml-2" onClick={props.modalState.hide}>
            Cancel
        </Button>

            {canEnterErpId && <Button variant="secondary" className="ml-2" onClick={handleAddErpId}>
                Enter ERP ID
            </Button>}

            {canRetrySync && (
                <Button variant="primary" className="ml-2" disabled={props.modalState.data.contract.isSyncingToErp} onClick={() => retryErpSync()}>
                    Retry Sync
                </Button>
            )}</>}

        {showManualErpIdModal && <ManualErpIdModal
            cancelButtonText={props.modalState.data.hideRetrySync ? "Cancel" : "Back"}
            erpId={currentErpId || props.modalState.data.contract.erpDisplayId || ''}
            onClose={() => {
                if (props.modalState.data.hideRetrySync) {
                    props.modalState.hide();
                } else {
                    setShowManualErpIdModal(false);
                }
                setErrors([]);
            }}
            onSubmit={handleSubmitErpId}
            errors={errors}
        />}
        {showConfirmModal && <ConfirmErpIdModal
            erpId={currentErpId}
            onClose={handleCancelConfirm}
            onConfirm={handleConfirmErpId}
        />}
        {showVerificationModal && response && <ErpIdVerificationModal
            erpId={currentErpId}
            isErpAvailable={response.isErpAvailable}
            message={response.message}
            onClose={handleCloseVerificationModal}
            onVerify={handleVerifyErpId}
            onSaveWithoutVerification={handleSaveWithoutVerification}
            onRetry={handleRetrySubmit} />}
    </div>;
})

export default SyncContractToErpRetryModal;